import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, Search } from "lucide-react";
import SEO from "@/components/SEO";

const TAG_FILTERS = [
  { slug: "bestseller", name: "Bestsellers" },
  { slug: "new-arrival", name: "New Arrivals" },
  { slug: "festive", name: "Festive" },
  { slug: "casual", name: "Casual" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sort, setSort] = useState("newest");
  const [localSearch, setLocalSearch] = useState(params.get("search") || "");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [subCategoriesOpen, setSubCategoriesOpen] = useState(true);

  const category = params.get("category") || "all";
  const subCategory = params.get("sub") || "";
  const tag = params.get("tag") || "";
  const color = params.get("color") || "";
  const size = params.get("size") || "";
  const fabric = params.get("fabric") || "";
  const search = params.get("search") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category !== "all") q.set("category", category);
    if (subCategory) q.set("sub_category", subCategory);
    if (tag) q.set("tag", tag);
    if (color) q.set("color", color);
    if (size) q.set("size", size);
    if (fabric) q.set("fabric", fabric);
    if (search) q.set("search", search);
    if (sort) q.set("sort", sort);
    api.get(`/products?${q.toString()}`).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [category, subCategory, tag, color, size, fabric, search, sort]);

  const filtered = useMemo(
    () => products.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]),
    [products, priceRange]
  );

  const topCats = Array.from(
    new Map(
      categories
        .filter((c) => !c.parent_slug)
        .map((c) => [
          String(c.name || c.slug || "")
            .trim()
            .toLowerCase(),
          c,
        ])
    ).values()
  );
  const subCats = categories.filter((c) => c.parent_slug === category);

  // Available filter options derived from current products
  const availableColors = useMemo(() => {
    const set = new Set();
    products.forEach((p) => (p.color_options || []).forEach((c) => set.add(c.name)));
    return Array.from(set).slice(0, 12);
  }, [products]);
  const availableSizes = useMemo(() => {
    const set = new Set();
    products.forEach((p) => (p.size_options || []).forEach((s) => set.add(s)));
    return Array.from(set).slice(0, 12);
  }, [products]);
  const availableFabrics = useMemo(() => {
    const set = new Set();
    products.forEach((p) => p.fabric && set.add(p.fabric));
    return Array.from(set).slice(0, 12);
  }, [products]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === "category") next.delete("sub");
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    updateParam("search", localSearch);
  };

  const clearAll = () => setParams(new URLSearchParams());

  const activeFilters = [category !== "all" && category, subCategory, tag, color, size, fabric].filter(Boolean).length;


  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#F6FCFA_0%,#FFFFFF_28%,#FFF8FB_65%,#F7FCFB_100%)] px-4 pb-8 pt-[116px] sm:px-6 sm:pb-10 sm:pt-[120px] lg:px-8 lg:pb-12 lg:pt-[132px]">
      <div className="mx-auto max-w-[1500px]">
      <SEO
        title={category === "all" ? "Shop All Products" : (categories.find((c) => c.slug === category)?.name || "Shop")}
        description={
          category === "all"
            ? "Browse the full Nityavastra collection — handwoven sarees, cotton bedsheets, and daily home essentials, all crafted in India."
            : `Shop ${categories.find((c) => c.slug === category)?.name || category} at Nityavastra. Handcrafted, GST-invoiced, ships pan-India.`
        }
      />
{filtersOpen && (
        <div
          className="fixed inset-0 z-[70] lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Shop filters"
        >
          <button
            type="button"
            aria-label="Close filters"
            onClick={() => setFiltersOpen(false)}
            className="absolute inset-0 bg-black/40"
          />

          <aside className="absolute right-0 top-0 h-full w-[88vw] max-w-md overflow-y-auto border-l border-[#DCEBE7] bg-[#F8FCFB] shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#DCEBE7] bg-[linear-gradient(135deg,#0F6A66,#126F78)] px-5 py-4 text-white shadow-lg backdrop-blur">
              <div>
                <p className="uppercase text-[10px] tracking-[0.25em] text-[#0F6A66]">
                  Refine
                </p>
                <h2 className="font-serif text-2xl text-[#173A3A]">
                  Filters
                </h2>
              </div>

              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                aria-label="Close filters"
                className="w-10 h-10 rounded-full border border-[#DCE8E5] bg-white flex items-center justify-center text-xl text-[#173A3A]"
              >
                ×
              </button>
            </div>

            <div className="p-5 space-y-8">
              {activeFilters > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  data-testid="mobile-clear-filters-btn"
                  className="text-xs text-[#0F6A66] underline hover:text-[#0A5653]"
                >
                  Clear all filters ({activeFilters})
                </button>
              )}

              <div>
                <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">
                  Search
                </h3>
                <form onSubmit={submitSearch} className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#667B79]" />
                  <Input
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    placeholder="Search products..."
                    data-testid="mobile-shop-search-input"
                    className="h-11 rounded-xl border-[#DCE8E5] bg-white pl-9 focus-visible:ring-[#0F8F83]"
                  />
                </form>
              </div>

              <div>
                <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">
                  Categories
                </h3>

                <ul className="space-y-1">
                  <li>
                    <button
                      type="button"
                      onClick={() => updateParam("category", "")}
                      data-testid="mobile-filter-cat-all"
                      className={`w-full text-left min-h-11 px-3 rounded-md text-sm transition-colors ${
                        category === "all"
                          ? "bg-[#E8F6F2] text-[#0F6A66] font-medium"
                          : "text-[#173A3A] hover:bg-[#E8F6F2]"
                      }`}
                    >
                      All Products
                    </button>
                  </li>

                  {topCats.map((c) => (
                    <li key={c.slug}>
                      <button
                        type="button"
                        onClick={() => updateParam("category", c.slug)}
                        data-testid={`mobile-filter-cat-${c.slug}`}
                        className={`w-full text-left min-h-11 px-3 rounded-md text-sm transition-colors ${
                          category === c.slug
                            ? "bg-[#E8F6F2] text-[#0F6A66] font-medium"
                            : "text-[#173A3A] hover:bg-[#E8F6F2]"
                        }`}
                      >
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {subCats.length > 0 && (
                <div>
                  <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">
                    {categories.find((c) => c.slug === category)?.name} sub-categories
                  </h3>

                  <ul className="space-y-1">
                    {subCats.map((c) => (
                      <li key={c.slug}>
                        <button
                          type="button"
                          onClick={() => updateParam("sub", subCategory === c.slug ? "" : c.slug)}
                          data-testid={`mobile-filter-sub-${c.slug}`}
                          className={`w-full text-left min-h-11 px-3 rounded-md text-sm transition-colors ${
                            subCategory === c.slug
                              ? "bg-[#E8F6F2] text-[#0F6A66] font-medium"
                              : "text-[#173A3A] hover:bg-[#E8F6F2]"
                          }`}
                        >
                          {c.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">
                  Tags
                </h3>

                <div className="flex flex-wrap gap-2">
                  {TAG_FILTERS.map((t) => (
                    <button
                      type="button"
                      key={t.slug}
                      onClick={() => updateParam("tag", tag === t.slug ? "" : t.slug)}
                      data-testid={`mobile-filter-tag-${t.slug}`}
                      className={`px-3 py-2 text-xs rounded-full border transition-colors ${
                        tag === t.slug
                          ? "bg-[#D84C8B] text-white border-[#D84C8B] shadow-sm"
                          : "bg-white border-[#DCE8E5] hover:border-[#0F6A66]"
                      }`}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              {availableColors.length > 0 && (
                <div>
                  <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">
                    Color
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availableColors.map((c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => updateParam("color", color === c ? "" : c)}
                        data-testid={`mobile-filter-color-${c}`}
                        className={`px-3 py-2 text-xs rounded-full border transition-colors ${
                          color === c
                            ? "bg-[#173A3A] text-white border-[#173A3A]"
                            : "bg-white border-[#DCE8E5] hover:border-[#0F6A66]"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {availableSizes.length > 0 && (
                <div>
                  <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">
                    Size
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map((itemSize) => (
                      <button
                        type="button"
                        key={itemSize}
                        onClick={() => updateParam("size", size === itemSize ? "" : itemSize)}
                        data-testid={`mobile-filter-size-${itemSize}`}
                        className={`px-3 py-2 text-xs rounded-full border transition-colors ${
                          size === itemSize
                            ? "bg-[#173A3A] text-white border-[#173A3A]"
                            : "bg-white border-[#DCE8E5] hover:border-[#0F6A66]"
                        }`}
                      >
                        {itemSize}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {availableFabrics.length > 0 && (
                <div>
                  <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">
                    Fabric
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availableFabrics.map((f) => (
                      <button
                        type="button"
                        key={f}
                        onClick={() => updateParam("fabric", fabric === f ? "" : f)}
                        data-testid={`mobile-filter-fabric-${f}`}
                        className={`px-3 py-2 text-xs rounded-full border transition-colors ${
                          fabric === f
                            ? "bg-[#173A3A] text-white border-[#173A3A]"
                            : "bg-white border-[#DCE8E5] hover:border-[#0F6A66]"
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">
                  Price Range
                </h3>

                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  min={0}
                  max={10000}
                  step={100}
                  data-testid="mobile-price-slider"
                />

                <div className="flex justify-between text-xs text-[#667B79] mt-3">
                  <span>₹{priceRange[0]}</span>
                  <span>₹{priceRange[1]}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="w-full min-h-12 rounded-full bg-[linear-gradient(90deg,#FF6B5F,#D84C8B)] text-white text-xs uppercase tracking-[0.18em] font-semibold shadow-lg shadow-[#D84C8B]/15"
              >
                Show {filtered.length} Products
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 lg:min-h-[calc(100vh-64px)] lg:grid-cols-12 lg:gap-10">
        {/* Sidebar */}
        <div className="h-full lg:col-span-3">
          <aside className="nv-shop-filter-sidebar h-fit self-start space-y-4 rounded-[26px] border border-[#DCEBE7] bg-white p-5 shadow-[0_18px_55px_-38px_rgba(15,106,102,0.55)] sm:p-6 lg:col-span-3 lg:sticky lg:top-[110px] lg:z-20">
            <div className="-mx-5 mb-6 overflow-hidden rounded-t-[25px] bg-[linear-gradient(135deg,#0F6A66_0%,#147B7B_55%,#1D8C82_100%)] px-5 py-5 text-white sm:-mx-6 sm:px-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#BFF3EA]">
                Curate Your Look
              </p>
              <h2 className="mt-1 font-serif text-2xl">
                Refine Your Style
              </h2>
              <div className="mt-3 h-1 w-16 rounded-full bg-[linear-gradient(90deg,#E9B949,#FF6B5F,#D84C8B)]" />
            </div>
          {activeFilters > 0 && (
            <button
              onClick={clearAll}
              data-testid="clear-filters-btn"
              className="text-xs text-[#0F6A66] underline hover:text-[#0A5653]"
            >
              Clear all filters ({activeFilters})
            </button>
          )}

          <div>
            <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">Search</h3>
            <form onSubmit={submitSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#667B79]" />
              <Input
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder="Search products..."
                data-testid="shop-search-input"
                className="bg-[#F9FCFB] pl-9 border-[#DCE8E5] rounded-xl focus-visible:ring-[#0F8F83]"
              />
            </form>
          </div>

          <div>
            <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">Categories</h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => updateParam("category", "")}
                  data-testid="filter-cat-all"
                  className={`w-full text-left py-1.5 text-sm transition-colors ${
                    category === "all" ? "text-[#0F6A66] font-medium" : "text-[#173A3A] hover:text-[#0F6A66]"
                  }`}
                >
                  All Products
                </button>
              </li>
              {topCats.map((c) => (
                <li key={c.slug}>
                  <button
                    onClick={() => updateParam("category", c.slug)}
                    data-testid={`filter-cat-${c.slug}`}
                    className={`w-full text-left py-1.5 text-sm transition-colors ${
                      category === c.slug ? "text-[#0F6A66] font-medium" : "text-[#173A3A] hover:text-[#0F6A66]"
                    }`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {subCats.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-[#DCE8E5] bg-[#FAFDFC]">
              <button
                type="button"
                onClick={() =>
                  setSubCategoriesOpen((current) => !current)
                }
                aria-expanded={subCategoriesOpen}
                className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left transition-colors hover:bg-[#EEF8F5]"
              >
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0F8F83]">
                    {categories.find((c) => c.slug === category)?.name}
                  </p>

                  <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#536C69]">
                    Sub-Categories
                  </p>
                </div>

                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-[#0F6A66] transition-transform duration-200 ${
                    subCategoriesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {subCategoriesOpen && (
                <div className="max-h-24 overflow-y-auto border-t border-[#DCE8E5] px-2 py-2">
                  <ul className="space-y-1">
                    {subCats.map((c) => (
                      <li key={c.slug}>
                        <button
                          type="button"
                          onClick={() =>
                            updateParam(
                              "sub",
                              subCategory === c.slug ? "" : c.slug
                            )
                          }
                          data-testid={`filter-sub-${c.slug}`}
                          className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                            subCategory === c.slug
                              ? "bg-[#0F6A66] font-medium text-white"
                              : "text-[#315956] hover:bg-[#E8F6F2] hover:text-[#0F6A66]"
                          }`}
                        >
                          {c.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

                    <div>
            <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {TAG_FILTERS.map((t) => (
                <button
                  key={t.slug}
                  onClick={() => updateParam("tag", tag === t.slug ? "" : t.slug)}
                  data-testid={`filter-tag-${t.slug}`}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    tag === t.slug
                      ? "bg-[#D84C8B] text-white border-[#D84C8B] shadow-sm"
                      : "bg-white border-[#DCE8E5] hover:border-[#0F6A66]"
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          {availableColors.length > 0 && (
            <div>
              <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">Color</h3>
              <div className="flex flex-wrap gap-2">
                {availableColors.map((c) => (
                  <button
                    key={c}
                    onClick={() => updateParam("color", color === c ? "" : c)}
                    data-testid={`filter-color-${c}`}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      color === c
                        ? "bg-[#173A3A] text-white border-[#173A3A]"
                        : "bg-white border-[#DCE8E5] hover:border-[#0F6A66]"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {availableSizes.length > 0 && (
            <div>
              <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">Size</h3>
              <div className="flex flex-wrap gap-2">
                {availableSizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateParam("size", size === s ? "" : s)}
                    data-testid={`filter-size-${s}`}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      size === s
                        ? "bg-[#173A3A] text-white border-[#173A3A]"
                        : "bg-white border-[#DCE8E5] hover:border-[#0F6A66]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {availableFabrics.length > 0 && (
            <div>
              <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">Fabric</h3>
              <div className="flex flex-wrap gap-2">
                {availableFabrics.map((f) => (
                  <button
                    key={f}
                    onClick={() => updateParam("fabric", fabric === f ? "" : f)}
                    data-testid={`filter-fabric-${f}`}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      fabric === f
                        ? "bg-[#173A3A] text-white border-[#173A3A]"
                        : "bg-white border-[#DCE8E5] hover:border-[#0F6A66]"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="uppercase text-xs tracking-[0.2em] font-medium mb-4 text-[#667B79]">Price Range</h3>
            <Slider value={priceRange} onValueChange={setPriceRange} min={0} max={10000} step={100} data-testid="price-slider" />
            <div className="flex justify-between text-xs text-[#667B79] mt-3">
              <span>₹{priceRange[0]}</span>
              <span>₹{priceRange[1]}</span>
            </div>
          </div>

        </aside>
        </div>

        {/* Grid */}
        <div className="min-w-0 pt-5 sm:pt-6 lg:col-span-9 lg:col-start-4 lg:pt-5">

          <div className="mb-4 flex justify-end">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger
                className="h-10 w-[150px] rounded-xl border-[#D8E5E2] bg-[#F9FCFB] text-[#173A3A] shadow-none sm:w-[190px]"
                data-testid="shop-sort"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>


          {loading ? (
            <div className="grid grid-cols-2 gap-x-2 gap-y-4 sm:gap-x-3 sm:gap-y-5 md:grid-cols-3 xl:grid-cols-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl border border-[#E3EEEB] bg-[linear-gradient(135deg,#EEF7F4,#F8FBFA,#FFF3F6)]" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center text-[#667B79]">No products match your filters.</div>
          ) : (
            <div className="grid grid-cols-2 gap-x-2 gap-y-4 sm:gap-x-3 sm:gap-y-5 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
